"use client";

import { motion, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import { Target, Compass } from "lucide-react";

// ── Data ────────────────────────────────────────────────────────────────────

const CARDS = [
  {
    id: "mission",
    eyebrow: "Our Mission",
    icon: Target,
    title: "Make real gold ownership open to everyone.",
    body:
      "We're on a mission to give every saver in the UAE a simple, safe way to own real 24K gold. No large minimums. No storage worries. Just fractional ownership of real metal, vaulted in Dubai.",
    points: [
      "Fractional ownership from any amount",
      "Insured, allocated storage in Dubai",
      "Shariah compliant and interest free",
    ],
  },
  {
    id: "vision",
    eyebrow: "Our Vision",
    icon: Compass,
    title: "The most trusted way to save in gold.",
    body:
      "We see a future where owning gold is as easy as sending a message. A world where anyone can protect and grow their wealth with real metal, on a platform they trust because every gram is auditable and every price is live.",
    points: [
      "On chain records for complete transparency",
      "Live rates, updated in real time",
      "Physical delivery whenever you choose",
    ],
  },
];

// ── Motion ──────────────────────────────────────────────────────────────────

const container: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.05 } },
};

function fadeUp(reduce: boolean): Variants {
  return {
    hidden: { opacity: 0, y: reduce ? 0 : 22 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: [0.2, 0, 0, 1] },
    },
  };
}

// ── Section ─────────────────────────────────────────────────────────────────

export function AboutMission() {
  const reduce = useReducedMotion() ?? false;

  return (
    <motion.section
      className="relative overflow-hidden bg-neutral-0 pb-16 pt-16 sm:pb-20 sm:pt-20 lg:pb-24 lg:pt-24"
      aria-labelledby="about-mission-heading"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
      variants={container}
    >
      {/* Soft ambient glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[240px] bg-[radial-gradient(60%_100%_at_50%_0%,rgba(14,58,138,0.06),transparent_70%)]"
      />

      <div className="relative mx-auto max-w-[1200px] px-6 sm:px-10 lg:px-8">
        {/* Header */}
        <motion.div
          variants={fadeUp(reduce)}
          className="mx-auto mb-12 max-w-[720px] text-center"
        >
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-500">
            What drives us
          </p>
          <h2
            id="about-mission-heading"
            className="text-[clamp(1.75rem,3.5vw,2.5rem)] font-semibold leading-[1.15] tracking-[-0.015em] text-primary-900"
          >
            Our <span className="font-instrument italic font-normal">mission</span>{" "}
            and <span className="font-instrument italic font-normal">vision</span>.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-text-body sm:text-lg">
            The purpose behind the platform, and the future we&apos;re building
            toward.
          </p>
        </motion.div>

        {/* Cards */}
        <motion.div
          variants={container}
          className="grid gap-6 md:grid-cols-2 md:gap-7"
        >
          {CARDS.map(({ id, eyebrow, icon: Icon, title, body, points }) => (
            <motion.article
              key={id}
              variants={fadeUp(reduce)}
              className="group relative flex flex-col overflow-hidden rounded-card border border-[#e3ecfb] bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-primary-200 hover:shadow-[0_24px_50px_-28px_rgba(10,45,110,0.28)] sm:p-9"
            >
              {/* Icon */}
              <span
                className="flex h-12 w-12 items-center justify-center rounded-card-sm bg-primary-50 text-primary-600 transition-colors duration-300 group-hover:bg-primary-100"
                aria-hidden="true"
              >
                <Icon className="h-5 w-5" strokeWidth={2} />
              </span>

              {/* Eyebrow */}
              <p className="mt-5 text-xs font-semibold uppercase tracking-[0.16em] text-primary-500">
                {eyebrow}
              </p>

              {/* Title */}
              <h3 className="mt-3 text-[clamp(1.35rem,2.2vw,1.65rem)] font-bold leading-[1.2] tracking-[-0.01em] text-text-heading">
                {title}
              </h3>

              {/* Body */}
              <p className="mt-4 text-base leading-relaxed text-text-body">
                {body}
              </p>

              {/* Bullet points */}
              <ul className="mt-6 flex flex-col gap-2.5 border-t border-[#e3ecfb] pt-5">
                {points.map((point) => (
                  <li
                    key={point}
                    className="flex items-start gap-2.5 text-sm text-text-heading"
                  >
                    <span
                      className="mt-[0.5em] h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500"
                      aria-hidden="true"
                    />
                    <span className="leading-snug">{point}</span>
                  </li>
                ))}
              </ul>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
}
